/**
 * Data layer for the Perks section's dashboards.
 *
 * Today only the COMPANY panel has these pages, so the adapters live here and
 * container/B2B/CompanyAdmin/Perks/companyPerksApi.js supplies the company/v1
 * data source. Keeping the shaping here means a future super-admin version of
 * these dashboards reads the payload exactly the same way.
 *
 * EVERY adapter flattens `[{ date, x }]` trend arrays to plain number arrays.
 * ApexCharts renders NaN paths and takes the page down if it is handed the
 * objects, so the flattening belongs here rather than in each caller.
 */

/** Money for a stat card: whole pounds, thousands separated. */
export const formatMoney = (value) => {
  if (value == null || isNaN(value)) return '-';
  return `£${Math.round(value).toLocaleString()}`;
};

/** Minutes as "3h 15m" — call time runs past an hour often enough to matter. */
export const formatMinutes = (mins) => {
  if (mins == null || isNaN(mins)) return '-';
  const total = Math.round(mins);
  if (total < 60) return `${total}m`;
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
};

/** A percentage that may legitimately be null ("no plan, so no utilisation"). */
export const formatPercent = (value) => (value == null ? '-' : `${value}%`);

/** Which programmes this company has, plus the section-level gate. */
export const adaptAvailability = (data) => ({
  enabled: Boolean(data?.enabled),
  instantSupport: Boolean(data?.instantSupport),
  salarySacrifice: Boolean(data?.salarySacrifice),
  healthScheme: Boolean(data?.healthScheme),
  customPerks: Boolean(data?.customPerks),
  companyName: data?.companyName || null
});

/** Shared shaping for the Perks overview. */
export const adaptOverview = (data) => ({
  totalUsers: data.totalUsers ?? 0,
  reached: data.reached ?? 0,
  reach: data.reach ?? 0,
  multiProgramme: data.multiProgramme ?? 0,
  multiProgrammeRate: data.multiProgrammeRate ?? 0,
  programmeCountSplit: data.programmeCountSplit || [],
  programmes: data.programmes || [],
  availability: data.availability || null
});

/** Shared shaping for the Instant Support (EAP) dashboard. */
export const adaptInstantSupport = (data) => ({
  plan: data.plan || null,
  totalUsers: data.totalUsers ?? 0,

  totalCalls: data.totalCalls ?? 0,
  confirmedCalls: data.confirmedCalls ?? 0,
  callers: data.callers ?? 0,
  confirmationRate: data.confirmationRate ?? 0,

  loggedCalls: data.loggedCalls ?? 0,
  totalMinutes: data.totalMinutes ?? 0,
  avgCallMinutes: data.avgCallMinutes ?? 0,
  seatUtilisation: data.seatUtilisation ?? null,

  // Backend zero-fills the last 30 days; charts consume plain series.
  callsPerDay: (data.callsTrend || []).map((day) => day.calls ?? 0),
  minutesPerDay: (data.callsTrend || []).map((day) => day.minutes ?? 0),

  durationDistribution: data.durationDistribution || [],
  callsPerUserDistribution: data.callsPerUserDistribution || [],
  weekdayCalls: data.weekdayCalls || [],
  planHistory: data.planHistory || []
});

/**
 * Shared shaping for the two scheme dashboards. Salary Sacrifice and Health
 * Scheme return the same envelope — the health payload just carries two extra
 * keys, which pass through untouched for the page that wants them.
 */
export const adaptSchemeDashboard = (data) => ({
  totalUsers: data.totalUsers ?? 0,

  // Catalogue half.
  totalSchemes: data.totalSchemes ?? 0,
  activeSchemes: data.activeSchemes ?? 0,
  pendingSchemes: data.pendingSchemes ?? 0,
  totalSeats: data.totalSeats ?? 0,
  totalBilling: data.totalBilling ?? 0,
  totalPlans: data.totalPlans ?? 0,
  statusSplit: data.statusSplit || [],
  typeSplit: data.typeSplit || [],
  expiryDistribution: data.expiryDistribution || [],
  schemes: data.schemes || [],

  // Enrollment half.
  enrolled: data.enrolled ?? 0,
  activeEnrollments: data.activeEnrollments ?? 0,
  lapsedEnrollments: data.lapsedEnrollments ?? 0,
  enrolledByYou: data.enrolledByYou ?? 0,
  enrolledBySplit: data.enrolledBySplit || [],
  tenureDistribution: data.tenureDistribution || [],
  enrollmentsPerDay: (data.enrollmentsTrend || []).map((day) => day.enrollments ?? 0),
  perScheme: data.perScheme || [],

  reach: data.reach ?? 0,
  seatUtilisation: data.seatUtilisation ?? null,

  // Health Scheme only; harmless nulls for Salary Sacrifice.
  planMix: data.planMix || null,
  visibleSchemes: data.visibleSchemes ?? null
});

/** Shared shaping for the Custom Perks dashboard. */
export const adaptCustomPerks = (data) => ({
  totalUsers: data.totalUsers ?? 0,

  totalPerks: data.totalPerks ?? 0,
  approvedPerks: data.approvedPerks ?? 0,
  promotedPerks: data.promotedPerks ?? 0,
  withWebsite: data.withWebsite ?? 0,
  withPhone: data.withPhone ?? 0,
  statusSplit: data.statusSplit || [],

  enrolled: data.enrolled ?? 0,
  enrollments: data.enrollments ?? 0,
  totalClicks: data.totalClicks ?? 0,
  clickers: data.clickers ?? 0,
  avgClicksPerPerson: data.avgClicksPerPerson ?? 0,
  reach: data.reach ?? 0,
  openRate: data.openRate ?? 0,

  clicksDistribution: data.clicksDistribution || [],
  enrollmentsPerDay: (data.enrollmentsTrend || []).map((day) => day.enrollments ?? 0),
  topPerks: data.topPerks || [],
  perks: data.perks || []
});

/** Shared shaping for the user-detail page. */
export const adaptUserDetail = (data) => ({
  user: data.user,
  enrollments: data.enrollments || [],
  customPerks: data.customPerks || [],
  callsPerDay: (data.callsTrend || []).map((day) => day.calls ?? 0),
  recentCalls: data.recentCalls || []
});
